import { ErrorHandler, Injector, Injectable } from '@angular/core'
import { VersionCheckService } from './services/version-check.service'

@Injectable()
export class CastomErrorHandler implements ErrorHandler {
    private versionCheckService: VersionCheckService | null = null

    constructor(private injector: Injector) {
        // Use injector to get service to avoid circular dependency
        // We'll get it lazily when needed
    }

    private getVersionCheckService(): VersionCheckService | null {
        if (!this.versionCheckService) {
            try {
                this.versionCheckService = this.injector.get(VersionCheckService)
            } catch (e) {
                // Service might not be available in all contexts
                return null
            }
        }
        return this.versionCheckService
    }

    handleError(error: any): void {
        const chunkFailedMessage = /Loading chunk [\d]+ failed/
        const moduleScriptFailedMessage = /Failed to load module script/
        const dynamicImportFailedMessage = /Failed to fetch dynamically imported module/
        const mimeTypeError = /MIME type.*text\/html/

        const errorMessage = error.message || ''
        const errorStack = error.originalStack || error.stack || ''

        // Handle chunk/module loading errors specifically
        // These errors typically occur when:
        // 1. Old chunks are referenced but no longer exist (version mismatch)
        // 2. Server returns HTML (404 page) instead of JavaScript (MIME type error)
        // 3. Dynamic imports fail due to missing or outdated chunks
        const isChunkError =
            chunkFailedMessage.test(errorMessage) ||
            moduleScriptFailedMessage.test(errorMessage) ||
            dynamicImportFailedMessage.test(errorMessage) ||
            mimeTypeError.test(errorMessage) ||
            errorStack.indexOf('Error: Loading chunk') >= 0 ||
            errorStack.indexOf('Failed to load module script') >= 0 ||
            errorStack.indexOf('Failed to fetch dynamically imported module') >= 0

        if (isChunkError) {
            // Use version check service if available, otherwise fallback to event
            const versionService = this.getVersionCheckService()
            if (versionService) {
                versionService.handleChunkError()
            } else {
                document.dispatchEvent(
                    new CustomEvent('version-refresh-alert', {
                        detail: { data: { versionUpdate: true } }
                    })
                )
            }
        } else {
            // Log all other errors to console for debugging
            // Also log the stack trace if available
            if (error.stack) {
                console.error('Stack trace:', error.originalStack)
            }

            throw error
        }
    }
}
